<template>
  <responsive-drawer-layout v-if="dataLoaded">
    <template v-slot:drawer>
      <v-list dense>
        <v-list-item-group v-model="activeItem" mandatory>
          <v-list-item v-for="(color, title) in validNavigationItems" :key="'item-' + title" :style="color === 'none' && { display: 'none' }">
            <v-list-item-icon>
              <v-icon :color="color">{{ color === 'green' ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle' }} </v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ title }}</v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </template>

    <template v-slot:content>
      <editor-factory
        v-if="visibleWbConfig[activeItem]"
        namespace="wagenbuch"
        :title="$t(visibleWbConfig[activeItem].title)"
        :cells="visibleWbConfig[activeItem].cells"
      >
        <template v-slot:card-actions>
          <div class="card-actions">
            <v-btn v-if="activeItem !== 0" class="my-2" @click="onPreviousClick">
              {{ $t('back') }}
            </v-btn>
            <span v-else />
            <!-- Placeholder to make 'justify-content: space-between' work -->
            <v-btn v-if="activeItem !== Object.keys(validNavigationItems).length - 1" class="my-2" @click="onNextClick">
              {{ $t('next') }}
            </v-btn>
            <v-btn v-else color="red" style="color: white" class="mt-2" :disabled="isFormDisabled" @click="sendForm()">
              <v-icon v-if="formSent" left>mdi-loading mdi-spin</v-icon>
              {{ $t('send') }}
            </v-btn>
          </div>
        </template>
      </editor-factory>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import config from '@/common/config';
import common from '@/mixins/common';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import { mapState } from 'vuex';
import configMixin from '../../../mixins/configMixin';
import EditorFactory from '../wb-config/WBUDEditorFactory.vue';
import { findParentKlassifizierung, findKlassifizierung } from './wb-common';
import wagenbuchService from '@/modules/wagenbuch/wagenbuchService';
import vehicleMixin from '@/mixins/vehicleMixin';

export default {
  name: 'SchadenFormular',
  mixins: [common, configMixin, vehicleMixin],
  components: {
    ResponsiveDrawerLayout,
    EditorFactory,
  },
  created() {
    this.init();
    this.setActiveSectionColorIfOnlyOptionalCells();
  },
  data() {
    return {
      activeItem: 0,
      formDisabled: false,
      formSent: false,
      validNavigationItems: {},
    };
  },
  computed: {
    ...mapState('wagenbuch', ['klassifizierungenByFzgTypeIdFk', 'selectedFahrzeugObj', 'reports', 'wbConfigs', 'navigationItems', 'formInput', 'dataLoaded']),
    isFormDisabled() {
      return this.formDisabled || this.formSent;
    },
    klassifizierungen() {
      if (!this.vehicle) return [];
      if (!this.klassifizierungenByFzgTypeIdFk[this.vehicle.typIdFk]) return [];
      return this.klassifizierungenByFzgTypeIdFk[this.vehicle.typIdFk].filter((k) => k.instance === this.classificationInstance);
    },
    wbType() {
      return this.$route.params.wbType;
    },
    wbConfig() {
      if (!this.wbType || !this.wbConfigs || !this.wbConfigs[this.wbType]) return [];
      return this.wbConfigs[this.wbType].config ? this.wbConfigs[this.wbType].config : [];
    },
    classificationInstance() {
      if (!this.wbConfigs || !this.wbType || !this.wbConfigs[this.wbType]) return 1;
      return this.wbConfigs[this.wbType].classificationInstance ? this.wbConfigs[this.wbType].classificationInstance : 1;
    },
    visibleWbConfig() {
      let config = this.wbConfig ?? [];

      // find cell WBUDPaintCell and is the only cell in that group
      config = config.filter((item) => {
        if (item.cells.length === 1) {
          const isPaintCell = item.cells[0].cellName === 'WBUDPaintCell';

          if (isPaintCell) {
            return this.getSkizzeUrl() ? true : false;
          }
        }
        return true;
      });

      return config;
    },
    skizzenByFzName() {
      return this.$store.state['wagenbuch'].skizzenByFzName || [];
    },
  },
  methods: {
    init() {
      if (!this.dataLoaded || !this.wbConfigs || !this.wbConfigs[this.wbType]) return;

      const items = {};

      this.wbConfigs[this.wbType].config.forEach((nav) => {
        const OPTIONAL_SECTION_COLOR = 'orange';
        const MANDATORY_SECTION_COLOR = 'orange';

        let isMinOneCellMandatory = nav.cells.some((cell) => cell.mandatory === true);

        if (nav.hideNavigationDrawer === true) {
          items[this.$t(nav.navigationTitle)] = 'none';
        } else {
          items[this.$t(nav.navigationTitle)] = isMinOneCellMandatory ? MANDATORY_SECTION_COLOR : OPTIONAL_SECTION_COLOR;
        }
      });

      this.validNavigationItems = items;
      this.onUpdate();
    },
    async sendForm() {
      this.formSent = true;

      const moduleConfig = this.getModuleConfig('Wagenbuch', this.$route.params.instance);

      const formData = {
        fzName: this.vehicle.fzName,
        ...this.formInput,
      };

      if (formData.klassifizierung !== undefined) {
        //Overwrite klassifizierung number with hierarchy text
        formData.klassifizierung = findParentKlassifizierung(formData.klassifizierung, this.klassifizierungen, '');
      }

      await this.$store.dispatch('wagenbuch/sendSchadenformular', {
        instance: this.$route.params.instance,
        wbType: this.$route.params.wbType,
        module: moduleConfig.title,
        formData,
      });
      if (this.$route.name === 'ereignis') {
        this.$router.push({ name: 'wagenbuch', params: { fzId: this.vehicle.id }, query: { tab: 'ereignis' } });
      } else {
        this.$router.push({ name: 'wagenbuch', params: { fzId: this.vehicle.id }, query: { tab: 'mangel' } });
        // this.$router.push({name:''}`/wagenbuch/${this.$route.params.wbType}/${this.vehicle.id}?tab=${this.$route.params.wbType}`);
      }

      this.formSent = false;
    },
    /**
     * Sets the active section color to green only if no cell is mandatory. Used to mark a section green when next button is clicked.
     */
    setActiveSectionColorIfOnlyOptionalCells() {
      if (!this.visibleWbConfig || this.visibleWbConfig.length === 0) return false;

      const activeConfig = this.visibleWbConfig[this.activeItem];
      if (!activeConfig) return false;

      const mandatoryCellsActiveSection = wagenbuchService.getMandatoryCells(activeConfig.cells);
      if (mandatoryCellsActiveSection.length === 0) {
        this.$set(this.validNavigationItems, this.$t(activeConfig.navigationTitle), 'green');
        return true;
      } else {
        return false;
      }
    },
    onPreviousClick() {
      let previousItem = this.visibleWbConfig[this.activeItem - 1];
      if (previousItem.cells && previousItem.cells.length > 0 && previousItem.cells[0].cellName === 'WBVideoCell') {
        this.activeItem = Math.max(0, this.activeItem - 2);
      } else {
        this.activeItem = Math.max(0, this.activeItem - 1);
      }
    },
    onNextClick() {
      this.setActiveSectionColorIfOnlyOptionalCells();
      this.activeItem += wagenbuchService.getActiveItemIndexIncrease({
        wbConfig: this.visibleWbConfig,
        activeItemIndex: this.activeItem,
        formInput: this.formInput,
        klassifizierungen: this.klassifizierungen,
        findKlassifizierung,
      });
    },
    onUpdate() {
      this.visibleWbConfig
        .filter((config) => this.validNavigationItems[this.$t(config.navigationTitle)] !== 'none')
        .forEach((config) => {
          let color = 'green';
          const mandatoryCells = wagenbuchService.getMandatoryCells(config.cells);

          if (mandatoryCells.length > 0) {
            color = 'orange';
            if (mandatoryCells.every((cell) => wagenbuchService.isCellValueSet(cell, this.formInput))) {
              color = 'green';
            }
            this.$set(this.validNavigationItems, this.$t(config.navigationTitle), color); // hier war 'green' hardcoded
          }
        });
      this.setFormDisabled();
    },
    setFormDisabled() {
      this.formDisabled = Object.keys(this.validNavigationItems).some((k) => this.validNavigationItems[k] === 'orange');
    },
    getSkizzeUrl() {
      const currentFz = this.skizzenByFzName.find((r) => r.id.toString() === this.vehicle.id.toString());

      return currentFz && currentFz.filename && currentFz.checksum ? `${config.apiUrl}/medias/wagenbuch/${currentFz.filename}/${currentFz.checksum}` : null;
    },
  },
  watch: {
    dataLoaded() {
      this.init();
    },
    formInput: {
      deep: true,
      handler() {
        this.onUpdate();
      },
    },
    activeItem: {
      deep: true,
      immediate: true,
      handler() {
        this.setActiveSectionColorIfOnlyOptionalCells();
      },
    },
  },
};
</script>

<style scoped>
.card-actions {
  bottom: 0;
  width: 100%;
  justify-content: space-between;
  display: flex;
}
</style>
